import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import {
  List,
  Loader,
  Dimmer,
} from 'semantic-ui-react';
import { debounce } from 'lodash';
import findResult, { hasKey } from '@/utils/resultsHelper';
import { states } from '@/constants/lifecycle';
import FacetSidebarRow from '../components/FacetSidebarRow';
import { facetTypes } from '../constants';
import { updateFilterValues, performFacetedSearch } from '../searchHelper';

const FacetSidebar = ({
  code,
  category,
  facets,
  filter,
  searchStatus,
  searchConfigStatus,
  searchConfig,
  updateFilters,
  canUpdate,
  role,
  lastEdited,
  performSearch,
}) => {
  const debounceSearch = useMemo(() => debounce(performSearch, 500), [performSearch]);

  const handleFilterUpdate = (facetKey, newFilter) => {
    const newFilters = { ...filter };

    newFilters[facetKey] = newFilter;
    updateFilters(category, code, newFilters, facetKey);
    debounceSearch(category, code);
  };

  // this is a permission-locked facet
  const facetAllowedForUser = (permission) => {
    if (!permission) {
      return true;
    }
    const elements = permission.split('.');
    const selector = elements[0];
    const action = elements[1];
    return role.permissions[selector][action];
  };

  const isLoadingConfig = searchConfigStatus === states.PENDING;
  const isLoadingResults = searchStatus === states.PENDING;

  const isValidFacetType = (type) => (Object.values(facetTypes).includes(type));
  const hasFacetValues = (facetName) => (facets && hasKey(facets, facetName) && facets[facetName].length > 0);
  const hasFilterValues = (facetName) => (hasKey(filter, facetName) && filter[facetName].length > 0);

  const canDisplayRow = (facetConfig) => (
    facetConfig.enabled_sidebar
    && hasFacetValues(facetConfig.name)
    && isValidFacetType(facetConfig.type)
    && (canUpdate || hasFilterValues(facetConfig.name))
    && facetAllowedForUser(facetConfig.permission)
  );

  return (
    <Dimmer.Dimmable as={List} dimmed={isLoadingConfig}>
      {
        searchConfig.facets.map((facetConfig) => (
          canDisplayRow(facetConfig) && (
            <List.Item key={facetConfig.name}>
              <FacetSidebarRow
                category={category}
                code={code}
                facetKey={facetConfig.name}
                facet={facets[facetConfig.name]}
                filter={filter[facetConfig.name] || []}
                handleFilterUpdate={canUpdate && (handleFilterUpdate)}
                isLoading={isLoadingResults && facetConfig.name === lastEdited}
                facetConfig={facetConfig}
                canUpdate={canUpdate}
              />
            </List.Item>
          )
        ))
      }
      <Dimmer active={isLoadingConfig}>
        <Loader active size="tiny" />
      </Dimmer>
    </Dimmer.Dimmable>
  );
};

const mapStateToProps = ({
  auth: { role },
  search: { searches, searchConfig },
}, { category, code }) => {
  const item = findResult(searches, category, code) || { facets: {}, searchStatus: states.NONE };
  return (
    {
      facets: item.facets,
      filter: item.filter,
      searchStatus: item.searchStatus,
      searchConfigStatus: searchConfig ? searchConfig.readStatus : states.NONE,
      searchConfig: searchConfig ? searchConfig.config : {},
      lastEdited: item.lastEdited,
      canUpdate: item.permissions.can_update,
      role,
    }
  );
};

const mapDispatchToProps = {
  updateFilters: updateFilterValues,
  performSearch: performFacetedSearch,
};

export default connect(mapStateToProps, mapDispatchToProps)(FacetSidebar);
